import { type UIEvent, useEffect, useRef, useState } from 'react';

import { type BaseAppearance, BaseButton, BaseInput } from '../base';
import type { DesignCapabilityRendererContext } from './contracts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ScrollArea,
} from '../primitives';
import { Building2, Check, ChevronsUpDown, Layers3 } from 'lucide-react';
import { useMonkeysLocale } from '../../provider/context';
import { DESIGN_CAPABILITY_I18N_KEYS } from './i18n';

type Choice = { id: string; label: string; labelKey?: string; labelI18n?: Record<string, string>; count?: number };
const appearance: BaseAppearance = {
  preset: 'soft',
  density: 'default',
  radius: 'lg',
  border: 'subtle',
  background: 'soft',
};

export function ChoicePickerCapabilityRenderer({
  model,
  loading,
  translate,
  emitIntent,
  childrenBySlot,
}: DesignCapabilityRendererContext) {
  const { locale: language } = useMonkeysLocale();
  const [open, setOpen] = useState(false);
  const search = String((model.search as { value?: string } | undefined)?.value ?? '');
  const [draft, setDraft] = useState(search);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const allNodes = (model.nodes ?? []) as Choice[];
  const label = (node: Choice) => {
    if (node.labelKey) return translate(node.labelKey);
    const values = node.labelI18n ?? {};
    const baseLanguage = language.split('-')[0];
    return String(
      values[language] ||
        Object.entries(values).find(([key]) => key.split('-')[0] === baseLanguage)?.[1] ||
        values['zh-CN'] ||
        values.zh ||
        values['en-US'] ||
        values.en ||
        Object.values(values).find((value) => typeof value === 'string') ||
        node.label,
    ).trim();
  };
  const nodes = model.searchMode === 'local' && draft.trim() ? allNodes.filter(node => `${label(node)} ${node.id}`.toLocaleLowerCase(language).includes(draft.trim().toLocaleLowerCase(language))) : allNodes;
  const selectedId = String(model.selectedNodeId ?? '');
  const selected = allNodes.find((node) => node.id === selectedId);
  const selectedLabel = selected ? model.showSelectedId === false ? label(selected) : `${label(selected)} · ${selectedId}` : selectedId;
  const pagination = model.pagination as { hasMore: boolean; nextCursor: string | null; loadingMore: boolean };
  const pending = loading || pagination.loadingMore;
  useEffect(() => { clearTimeout(timer.current); setDraft(search); }, [search, selectedId]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const close = () => {
    setOpen(false);
    clearTimeout(timer.current);
    setDraft('');
    if (draft || search) emitIntent('capability.choice-picker.search', { value: '' });
  };
  const scroll = (event: UIEvent<HTMLDivElement>) => {
    const viewport = event.currentTarget;
    if (
      !pending &&
      pagination.hasMore &&
      pagination.nextCursor &&
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <= 64
    ) {
      emitIntent('capability.choice-picker.load-more', { cursor: pagination.nextCursor });
    }
  };
  const picker = (
    <DropdownMenu open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <DropdownMenuTrigger asChild>
        <BaseButton
          appearance={{ ...appearance, background: 'plain' }}
          tone="default"
          className="w-full max-w-full justify-between sm:w-[380px]"
          classNames={{ label: 'truncate text-left' }}
          label={selectedLabel || translate(String(model.placeholderKey))}
          endIcon={<ChevronsUpDown className="h-4 w-4 opacity-60" />}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] p-2"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <BaseInput
            appearance={appearance}
            placeholder={translate(String(model.searchPlaceholderKey))}
            value={draft}
            autoFocus
            onChange={(event) => {
              const value = event.target.value;
              setDraft(value);
              clearTimeout(timer.current);
              timer.current = setTimeout(
                () => emitIntent('capability.choice-picker.search', { value }),
                value.trim() ? Number(model.searchDebounceMs ?? 250) : 0,
              );
            }}
          />
        </div>
        {model.countLabelKey || model.statLabelKey ? <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          {model.countLabelKey ? <span>{translate(String(model.countLabelKey), { count: Number(model.total ?? 0) })}</span> : null}
          {model.statLabelKey ? <span>{translate(String(model.statLabelKey))}</span> : null}
        </div> : null}
        <ScrollArea className="mt-2 h-64 rounded-md border" onViewportScroll={scroll}>
          <div className="p-1">
            {!pending && nodes.length === 0 ? (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                {translate(String(model.emptyLabelKey))}
              </div>
            ) : (
              nodes.map((node) => (
                <button
                  key={node.id}
                  type="button"
                  className={`flex w-full items-start justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors ${node.id === selectedId ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (node.id !== selectedId) emitIntent('capability.choice-picker.select', { itemId: node.id });
                    close();
                  }}
                >
                  <div className="min-w-0">
                    <div className="line-clamp-1 font-medium">{label(node)}</div>
                    <div className="line-clamp-1 text-xs text-muted-foreground">{node.id}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {typeof node.count === 'number' ? <span
                      className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                      title={model.statLabelKey ? translate(String(model.statLabelKey)) : undefined}
                    >
                      {node.count}
                    </span> : null}
                    {node.id === selectedId ? (
                      <Check className="h-4 w-4 shrink-0" />
                    ) : (
                      <span className="h-4 w-4 shrink-0" />
                    )}
                  </div>
                </button>
              ))
            )}
            {pending ? (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                {translate(String(model.loadingLabelKey))}
              </div>
            ) : null}
          </div>
        </ScrollArea>
        {pagination.hasMore && pagination.nextCursor ? (
          <BaseButton
            className="mt-2 w-full"
            disabled={pending}
            label={translate(pending ? DESIGN_CAPABILITY_I18N_KEYS.pagination.loadingMore : DESIGN_CAPABILITY_I18N_KEYS.pagination.loadMore)}
            onClick={() => emitIntent('capability.choice-picker.load-more', { cursor: pagination.nextCursor })}
          />
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
  const hint = model.contextHint as
    | { selectedLabelKey: string; emptyLabelKey: string; icon: 'layers' | 'building' }
    | undefined;
  if (!hint) return picker;
  const Icon = hint.icon === 'building' ? Building2 : Layers3;
  return (
    <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
      {picker}
      {childrenBySlot.controls}
      <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate">
          {translate(selectedId ? hint.selectedLabelKey : hint.emptyLabelKey, {
            name: selected ? label(selected) : selectedId,
          })}
        </span>
      </div>
    </div>
  );
}
